import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { bookingsService } from '../../../services/api';

/**
 * UpdateItemForm - Form for updating existing booked rooms, beds, or courses
 */
function UpdateItemForm({ booking, entityType, selectedItem, onSuccess, onCancel }) {
  const [loading, setLoading] = useState(false);
  
  // Form state
  const [checkIn, setCheckIn] = useState('');
  const [checkOut, setCheckOut] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [basePrice, setBasePrice] = useState('');
  const [finalAmount, setFinalAmount] = useState('');
  const [status, setStatus] = useState('CONFIRMED');
  const [notes, setNotes] = useState('');

  // Initialize form with selected item data
  useEffect(() => {
    if (selectedItem) {
      if (entityType === 'room' || entityType === 'bed') {
        setCheckIn(selectedItem.checkIn?.split('T')[0] || '');
        setCheckOut(selectedItem.checkOut?.split('T')[0] || '');
        setBasePrice(selectedItem.basePrice || '');
        setFinalAmount(selectedItem.finalAmount || '');
        setStatus(selectedItem.status || 'CONFIRMED');
        setNotes(selectedItem.notes || '');
      } else if (entityType === 'course') {
        setStartDate(selectedItem.startDate?.split('T')[0] || '');
        setEndDate(selectedItem.endDate?.split('T')[0] || '');
        setQuantity(selectedItem.quantity || 1);
        setBasePrice(selectedItem.fee || '');
        setFinalAmount(selectedItem.finalAmount || '');
        setStatus(selectedItem.status || 'CONFIRMED');
        setNotes(selectedItem.notes || '');
      }
    }
  }, [selectedItem, entityType]);

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    setLoading(true);
    try {
      let response;
      const data = {};

      // Only include fields that have been changed
      if (entityType === 'room') {
        if (checkIn && checkIn !== selectedItem.checkIn?.split('T')[0]) {
          data.checkIn = checkIn + 'T15:00:00.000Z';
        }
        if (checkOut && checkOut !== selectedItem.checkOut?.split('T')[0]) {
          data.checkOut = checkOut + 'T11:00:00.000Z';
        }
        if (basePrice && parseFloat(basePrice) !== selectedItem.basePrice) {
          data.basePrice = parseFloat(basePrice);
        }
        if (finalAmount && parseFloat(finalAmount) !== selectedItem.finalAmount) {
          data.finalAmount = parseFloat(finalAmount);
        }
        if (status !== selectedItem.status) {
          data.status = status;
        }
        if (notes !== (selectedItem.notes || '')) {
          data.notes = notes.trim() || null;
        }

        response = await bookingsService.updateBookedRoom(booking.id, selectedItem.id, data);
        toast.success('Room updated successfully');
      } else if (entityType === 'bed') {
        if (checkIn && checkIn !== selectedItem.checkIn?.split('T')[0]) {
          data.checkIn = checkIn + 'T15:00:00.000Z';
        }
        if (checkOut && checkOut !== selectedItem.checkOut?.split('T')[0]) {
          data.checkOut = checkOut + 'T11:00:00.000Z';
        }
        if (basePrice && parseFloat(basePrice) !== selectedItem.basePrice) {
          data.basePrice = parseFloat(basePrice);
        }
        if (finalAmount && parseFloat(finalAmount) !== selectedItem.finalAmount) {
          data.finalAmount = parseFloat(finalAmount);
        }
        if (status !== selectedItem.status) {
          data.status = status;
        }
        if (notes !== (selectedItem.notes || '')) {
          data.notes = notes.trim() || null;
        }

        response = await bookingsService.updateBookedBed(booking.id, selectedItem.id, data);
        toast.success('Bed updated successfully');
      } else if (entityType === 'course') {
        if (startDate && startDate !== selectedItem.startDate?.split('T')[0]) {
          data.startDate = startDate + 'T09:00:00.000Z';
        }
        if (endDate && endDate !== selectedItem.endDate?.split('T')[0]) {
          data.endDate = endDate + 'T17:00:00.000Z';
        }
        if (quantity && parseInt(quantity) !== selectedItem.quantity) {
          data.quantity = parseInt(quantity);
        }
        if (basePrice && parseFloat(basePrice) !== selectedItem.fee) {
          data.fee = parseFloat(basePrice);
        }
        if (finalAmount && parseFloat(finalAmount) !== selectedItem.finalAmount) {
          data.finalAmount = parseFloat(finalAmount);
        }
        if (status !== selectedItem.status) {
          data.status = status;
        }
        if (notes !== (selectedItem.notes || '')) {
          data.notes = notes.trim() || null;
        }

        response = await bookingsService.updateBookedCourse(booking.id, selectedItem.id, data);
        toast.success('Course updated successfully');
      }

      if (Object.keys(data).length === 0) {
        toast.info('No changes detected');
        return;
      }

      if (onSuccess) {
        onSuccess(response);
      }
    } catch (error) {
      console.error('Error updating item:', error);
      toast.error(error.error || error.message || `Failed to update ${entityType}`);
    } finally {
      setLoading(false);
    }
  };

  // Calculate nights for rooms/beds
  const calculateNights = () => {
    if (!checkIn || !checkOut) return 0;
    const start = new Date(checkIn);
    const end = new Date(checkOut);
    return Math.ceil((end - start) / (1000 * 60 * 60 * 24));
  };

  // Auto-calculate final amount when base price or dates change (for rooms/beds)
  useEffect(() => {
    if ((entityType === 'room' || entityType === 'bed') && basePrice && checkIn && checkOut) {
      const start = new Date(checkIn);
      const end = new Date(checkOut);
      const nights = Math.ceil((end - start) / (1000 * 60 * 60 * 24));
      if (nights > 0) {
        const calculatedAmount = parseFloat(basePrice) * nights;
        setFinalAmount(calculatedAmount.toFixed(2));
      }
    }
  }, [basePrice, checkIn, checkOut, entityType]);

  if (!selectedItem) {
    return (
      <div style={{ backgroundColor: '#F5F3EB', padding: '20px', borderRadius: '8px', border: '1px solid #E3DFD3', textAlign: 'center' }}>
        <p style={{ color: '#5F6B61', fontSize: '14px' }}>Select a {entityType} to update</p>
      </div>
    );
  }

  return (
    <div style={{ backgroundColor: '#F5F3EB', padding: '20px', borderRadius: '8px', border: '1px solid #E3DFD3' }}>
      <h4 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: '600', color: '#36433B' }}>
        Update {entityType.charAt(0).toUpperCase() + entityType.slice(1)}
      </h4>

      {/* Current Item Info */}
      <div style={{ backgroundColor: '#F3E6CB', padding: '12px', borderRadius: '6px', marginBottom: '16px', border: '1px solid #B68235' }}>
        <div style={{ fontSize: '14px', fontWeight: '600', color: '#684A1F', marginBottom: '4px' }}>
          Current {entityType.charAt(0).toUpperCase() + entityType.slice(1)}:
        </div>
        <div style={{ fontSize: '13px', color: '#4F3818' }}>
          {entityType === 'room' && `Room ${selectedItem.roomNumber || selectedItem.room?.roomNumber}`}
          {entityType === 'bed' && `Bed ${selectedItem.bedNumber || selectedItem.bed?.bedNumber} in Room ${selectedItem.roomNumber || selectedItem.room?.roomNumber}`}
          {entityType === 'course' && `${selectedItem.courseName || selectedItem.course?.name}`}
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        {/* Date Fields for Rooms/Beds */}
        {(entityType === 'room' || entityType === 'bed') && (
          <div style={{ marginBottom: '16px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#36433B', marginBottom: '4px' }}>
                  Check-in Date
                </label>
                <input
                  type="date"
                  value={checkIn}
                  onChange={(e) => setCheckIn(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #D2CDBF',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#36433B', marginBottom: '4px' }}>
                  Check-out Date
                </label>
                <input
                  type="date"
                  value={checkOut}
                  onChange={(e) => setCheckOut(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #D2CDBF',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                />
              </div>
            </div>
            {checkIn && checkOut && (
              <div style={{ fontSize: '13px', color: '#5F6B61', fontStyle: 'italic' }}>
                Duration: {calculateNights()} night{calculateNights() !== 1 ? 's' : ''}
              </div>
            )}
          </div>
        )}

        {/* Date Fields for Courses */}
        {entityType === 'course' && (
          <div style={{ marginBottom: '16px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#36433B', marginBottom: '4px' }}>
                  Start Date
                </label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #D2CDBF',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#36433B', marginBottom: '4px' }}>
                  End Date
                </label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #D2CDBF',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                />
              </div>
            </div>
          </div>
        )}

        {/* Quantity (for courses) */}
        {entityType === 'course' && (
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#36433B', marginBottom: '4px' }}>
              Quantity
            </label>
            <input
              type="number"
              value={quantity}
              onChange={(e) => {
                const newQty = parseInt(e.target.value) || 1;
                setQuantity(newQty);
                setFinalAmount(basePrice * newQty || '');
              }}
              min="1"
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #D2CDBF',
                borderRadius: '6px',
                fontSize: '14px'
              }}
            />
          </div>
        )}

        {/* Pricing */}
        <div style={{ marginBottom: '16px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <div>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#36433B', marginBottom: '4px' }}>
                Base Price {entityType === 'room' || entityType === 'bed' ? '/night' : ''}
              </label>
              <input
                type="number"
                value={basePrice}
                onChange={(e) => {
                  setBasePrice(e.target.value);
                  if (entityType === 'course') {
                    setFinalAmount(e.target.value * quantity || '');
                  }
                }}
                min="0"
                step="0.01"
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #D2CDBF',
                  borderRadius: '6px',
                  fontSize: '14px'
                }}
              />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#36433B', marginBottom: '4px' }}>
                Final Amount
              </label>
              <input
                type="number"
                value={finalAmount}
                onChange={(e) => setFinalAmount(e.target.value)}
                min="0"
                step="0.01"
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #D2CDBF',
                  borderRadius: '6px',
                  fontSize: '14px'
                }}
              />
            </div>
          </div>
        </div>

        {/* Status */}
        <div style={{ marginBottom: '16px' }}>
          <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#36433B', marginBottom: '4px' }}>
            Status
          </label>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            style={{
              width: '100%',
              padding: '8px',
              border: '1px solid #D2CDBF',
              borderRadius: '6px',
              fontSize: '14px'
            }}
          >
            <option value="PENDING">Pending</option>
            <option value="CONFIRMED">Confirmed</option>
            <option value="CHECKEDIN">Checked In</option>
            <option value="CHECKEDOUT">Checked Out</option>
            <option value="CANCELLED">Cancelled</option>
          </select>
        </div>

        {/* Notes */}
        <div style={{ marginBottom: '16px' }}>
          <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#36433B', marginBottom: '4px' }}>
            Notes (optional)
          </label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows="3"
            style={{
              width: '100%',
              padding: '8px',
              border: '1px solid #D2CDBF',
              borderRadius: '6px',
              fontSize: '14px',
              resize: 'vertical'
            }}
            placeholder="Add update notes..."
          />
        </div>

        {/* Action Buttons */}
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
          <button
            type="button"
            onClick={onCancel}
            disabled={loading}
            style={{
              backgroundColor: 'white',
              color: '#36433B',
              border: '1px solid #D2CDBF',
              padding: '8px 16px',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: loading ? 'not-allowed' : 'pointer'
            }}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            style={{
              backgroundColor: loading ? '#A5A89B' : '#1A3E2A',
              color: 'white',
              border: 'none',
              padding: '8px 16px',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: loading ? 'not-allowed' : 'pointer'
            }}
          >
            {loading ? 'Updating...' : 'Update ' + entityType.charAt(0).toUpperCase() + entityType.slice(1)}
          </button>
        </div>
      </form>
    </div>
  );
}

export default UpdateItemForm;
